import { graphql } from '@api/generated';

import { getClient } from '@api/client';
import {
  CallRecording,
  CallRecordingSettingsOptions,
} from '@api/generated/graphql';

const GET_RECORDING_STATUS_TIMEOUT_IN_MS = 10_000;

const GET_RECORDING_QUERY = graphql(`
  query recordingStatus($kazooCallId: String!) {
    recordingStatus(kazooCallId: $kazooCallId) {
      status
      settings
      announceRecording
    }
  }
`);

/**
 * Will fetch the recording status for the provided `kazooCallId`
 * It will retry for up to 10 seconds if the recording status is not set
 * @param kazooCallId - remoteCallId for a call
 */
async function getRecordingStatus(
  kazooCallId: string,
): Promise<CallRecording | undefined> {
  const startTime = Date.now();
  const fetchRecordingStatus = async (
    retryCount = 0,
  ): Promise<CallRecording | undefined> => {
    const { data, error } = await getClient().query({
      query: GET_RECORDING_QUERY,
      variables: {
        kazooCallId,
      },
      fetchPolicy: 'network-only',
    });

    if (error) {
      throw new Error(error.message);
    }

    if (
      data?.recordingStatus &&
      data.recordingStatus.settings !== CallRecordingSettingsOptions.Unset
    ) {
      return data.recordingStatus;
    }

    // the PCS process that determines the recording settings can take some time,
    // retry if recording status is not set or null
    if (Date.now() - startTime < GET_RECORDING_STATUS_TIMEOUT_IN_MS) {
      return new Promise((resolve) => {
        setTimeout(
          async () => {
            resolve(await fetchRecordingStatus(retryCount + 1));
          },
          1000 * (retryCount + 1),
        ); // retry with linear backoff
      });
    }

    return undefined;
  };

  return await fetchRecordingStatus();
}

export default getRecordingStatus;
